import React, { useMemo, useState } from 'react';
import { Play, Pause, Download, Share2, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';
import { useStore } from '@/lib/store';
import { themeColor } from '@/data/samples';
import { usePlayback } from '@/lib/playback';

function MiniWave({ color, playing, progress = 0 }) {
  const bars = useMemo(
    () => Array.from({ length: 42 }, (_, i) => 22 + Math.abs(Math.sin(i * 0.55)) * 58 + ((i * 17) % 19)),
    []
  );
  return (
    <div className="relative flex items-end h-12 gap-[2px]">
      {bars.map((base, i) => {
        const t = i / bars.length;
        const ahead = t <= progress;
        const pulse = playing ? 1 + 0.35 * Math.sin((progress * 18 + i) * 0.9) : 1;
        const height = Math.min(100, base * (playing && ahead ? pulse : 1));
        return (
          <span
            key={i}
            className={`wave-bar ${playing ? 'playing' : ''}`}
            style={{
              height: `${height}%`,
              background: color,
              animationDelay: `${i * 18}ms`,
              opacity: playing ? (ahead ? 0.95 : 0.28) : 0.55,
            }}
          />
        );
      })}
      {playing && (
        <div
          className="absolute top-0 bottom-0 w-px bg-white/80 pointer-events-none"
          style={{ left: `${Math.min(100, progress * 100)}%` }}
        />
      )}
    </div>
  );
}

export default function HighlightCards({ highlights, source }) {
  const { state, setState } = useStore();
  const playback = usePlayback();
  const [copied, setCopied] = useState(null);
  const [dled, setDled] = useState(null);
  const [shared, setShared] = useState(null);

  const onCopyQuote = async (h) => {
    try {
      await navigator.clipboard.writeText(h.quote || h.excerpt || h.title || '');
      toast.success('Quote copied to clipboard');
      setCopied(h.id);
      setTimeout(() => setCopied(null), 1500);
    } catch { toast.error('Copy failed'); }
  };

  const onDownload = async (h) => {
    setDled(h.id);
    const ok = await playback.download(h, { source });
    if (ok) {
      toast.success(`Downloading clip · ${h.title}.mp3`, {
        description: 'Tagged with SIMNOPSIS title and logo',
      });
    } else {
      toast.info('Clip download unavailable for this highlight');
    }
    setTimeout(() => setDled(null), 1600);
  };

  const onShare = async (h) => {
    const text = `"${h.quote || h.excerpt || h.title}" — ${source} · ${h.start} · via SIMNOPSIS`;
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Shareable quote copied');
      setShared(h.id);
      setTimeout(() => setShared(null), 1600);
    } catch { toast.error('Copy failed'); }
  };

  return (
    <div className="grid md:grid-cols-3 gap-5">
      {highlights.map((h, idx) => {
        const c = themeColor(h.theme);
        const active = state.activeHighlightId === h.id;
        const isThis = playback.highlightId === h.id;
        const isPlaying = isThis && playback.isPlaying;
        const timerLabel = isThis
          ? `${playback.clock} / ${playback.durationClock || h.length}`
          : h.length;

        return (
          <article
            key={h.id}
            className={`hl-card anim-slide-up ${active || isThis ? 'active-sync' : ''}`}
            style={{ animationDelay: `${idx * 80}ms` }}
            onMouseEnter={() => setState({ activeHighlightId: h.id })}
            onMouseLeave={() => setState({ activeHighlightId: null })}
            data-testid={`highlight-card-${h.id}`}
          >
            <div className="flex items-center gap-2 mb-3">
              <span
                className="tag-pill"
                style={{ background: `${c}22`, color: c, border: `1px solid ${c}44` }}
              >
                {h.themeName}
              </span>
              <span className="ml-auto font-mono text-xs text-white/45 tabular-nums">
                {isThis ? playback.clock : h.start}
              </span>
            </div>

            <h3 className="font-display font-semibold text-lg leading-tight mb-2 pr-2">{h.title}</h3>

            {(h.quote || h.excerpt) && (
              <blockquote
                className="text-white/70 text-sm leading-relaxed italic mb-4 relative group cursor-copy"
                onClick={() => onCopyQuote(h)}
                aria-label="Copy quote to clipboard"
                data-testid={`quote-copy-${h.id}`}
              >
                <span className="text-white/25 mr-1">"</span>{h.quote || h.excerpt}<span className="text-white/25 ml-1">"</span>
                <span className="absolute -top-1 -right-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  {copied === h.id ? <Check className="h-3.5 w-3.5 text-[#7EE06D]" /> : <Copy className="h-3.5 w-3.5 text-white/40" />}
                </span>
              </blockquote>
            )}

            <div className="mb-4">
              <MiniWave color={c} playing={isPlaying} progress={isThis ? playback.progress : 0} />
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    setState({ activeHighlightId: h.id });
                    playback.toggle(h);
                  }}
                  className="h-9 w-9 rounded-full bg-white/5 border border-white/10 flex items-center justify-center hover:bg-white/10 transition-colors"
                  aria-label={isPlaying ? `Pause ${h.title}` : `Play ${h.title}`}
                  data-testid={`play-btn-${h.id}`}
                >
                  {isPlaying
                    ? <Pause className="h-3.5 w-3.5 text-white/90" />
                    : <Play className="h-3.5 w-3.5 text-white/90" />}
                </button>
                <button
                  onClick={() => onDownload(h)}
                  className="h-9 w-9 rounded-full bg-white/5 border border-white/10 flex items-center justify-center hover:bg-white/10 transition-colors"
                  aria-label={`Download ${h.title}`}
                  data-testid={`download-${h.id}`}
                >
                  {dled === h.id ? <Check className="h-3.5 w-3.5 text-[#7EE06D]" /> : <Download className="h-3.5 w-3.5 text-white/80" />}
                </button>
                <button
                  onClick={() => onShare(h)}
                  className="h-9 w-9 rounded-full bg-white/5 border border-white/10 flex items-center justify-center hover:bg-white/10 transition-colors"
                  aria-label={`Share ${h.title}`}
                  data-testid={`share-${h.id}`}
                >
                  {shared === h.id ? <Check className="h-3.5 w-3.5 text-[#7EE06D]" /> : <Share2 className="h-3.5 w-3.5 text-white/80" />}
                </button>
              </div>
              <div className="flex items-center gap-3">
                <span className="font-mono text-xs text-white/40 tabular-nums">{timerLabel}</span>
                <span
                  className="tag-pill"
                  style={{ background: 'linear-gradient(90deg, rgba(32,184,242,0.2), rgba(126,224,109,0.2))', color: 'white', border: '1px solid rgba(255,255,255,0.1)' }}
                >
                  {Number(h.rating || 0).toFixed(1)}
                </span>
              </div>
            </div>
          </article>
        );
      })}
    </div>
  );
}
